import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { House } from "@phosphor-icons/react";

const AUTH_BG =
  "https://images.pexels.com/photos/17832175/pexels-photo-17832175.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1400";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("sofia@estate.com");
  const [password, setPassword] = useState("demo123");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(() => {
    if (typeof window === "undefined") return "";
    const params = new URLSearchParams(loc.search);
    return params.get("expired") === "1" ? "Your session expired. Please sign in again." : "";
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-ink-900 text-zinc-100">
      {/* Left image pane */}
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(10,10,12,0.6), rgba(10,10,12,0.95)), url(${AUTH_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 p-12 flex flex-col justify-between">
          <div className="flex items-center gap-2">
            <House size={28} weight="duotone" className="text-gold" />
            <span className="font-display text-3xl">Reagent Network</span>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">Private Network</div>
            <h1 className="font-display text-5xl leading-[1.05] tracking-tight max-w-md">
              An invitation-only network for real estate professionals.
            </h1>
            <p className="mt-4 text-zinc-400 max-w-md text-sm leading-relaxed">
              Track inventory, move deals, and exchange opportunities with the agents who matter.
            </p>
          </div>
        </div>
      </div>

      {/* Right form pane */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <House size={24} weight="duotone" className="text-gold" />
            <span className="font-display text-2xl">Reagent Network</span>
          </div>

          <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">Sign in</div>
          <h2 className="font-display text-4xl tracking-tight">Welcome back.</h2>
          <p className="text-zinc-500 text-sm mt-2">
            Enter your credentials to continue.
          </p>

          <form className="mt-10 space-y-5" onSubmit={onSubmit} data-testid="login-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-2 bg-ink-700 border-white/10 focus:border-gold focus-visible:ring-gold h-11"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-2 bg-ink-700 border-white/10 focus:border-gold focus-visible:ring-gold h-11"
              />
            </div>
            {err && <div className="text-sm text-red-400" data-testid="login-error">{err}</div>}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-gold text-ink-900 hover:bg-gold-light font-semibold tracking-wide"
            >
              {loading ? "Signing in..." : "Sign in →"}
            </Button>
          </form>

          <div className="mt-8 text-sm text-zinc-500 flex items-center justify-between">
            <Link to="/signup" className="text-gold hover:text-gold-light" data-testid="go-to-signup">
              Create an account
            </Link>
            <Link to="/forgot-password" className="text-zinc-400 hover:text-gold" data-testid="go-to-forgot">
              Forgot password?
            </Link>
          </div>

          <div className="mt-10 p-4 rounded-lg bg-white/[0.02] border hairline text-xs text-zinc-500">
            <div className="uppercase tracking-[0.2em] text-zinc-400 mb-1">Demo</div>
            sofia@estate.com · demo123 — try logging in as Sofia to see a populated account.
          </div>
        </div>
      </div>
    </div>
  );
}
